import posthog from 'posthog-js';

import type { EventName, EventProperties } from '@/lib/tracking/types';

import { isMarketingEvent } from './isMarketingEvent';
import { Tracker } from './Tracker';

declare global {
  interface Window {
    EncTracking?: {
      identify?: (params: {
        email?: string;
        userId?: string;
        firstName?: string;
        lastName?: string;
        language?: string;
      }) => void;
      clearCookies?: () => void;
    };
  }
}

export class FrontendTracker implements Tracker {
  private enabled: boolean;

  constructor() {
    this.enabled =
      Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY) && !process.env.NEXT_PUBLIC_DISABLE_POSTHOG;
  }

  // posthog-js is initialised once, by initPostHogIfAllowed (src/lib/tracking/posthog-client.ts).
  // This tracker only uses that instance (marketing events, identify, reset, captureException).
  init() {
    if (!this.enabled) {
      console.info('FrontendTracker disabled (missing NEXT_PUBLIC_POSTHOG_KEY)');
    }
  }

  // Only marketing events: every other event goes server-side (ServerSideTracker).
  // posthog-js adds $current_url, $referrer, campaign params and $session_id, and on a
  // marketing page it is loaded only after the visitor accepted cookies.
  trackEvent<T extends EventName>(event: T, properties: EventProperties<T>): void {
    if (!this.enabled || !posthog.__loaded || !isMarketingEvent(event)) {
      return;
    }
    posthog.capture(event, properties);
  }

  // ADD: Error capture method
  captureException(error: Error, properties?: Record<string, any>): void {
    if (!this.enabled) {
      return;
    }

    if (posthog.__loaded) {
      posthog.captureException(error, properties);
    }
  }

  async identify(traits?: Record<string, any>, _anonymousId?: string) {
    if (!this.enabled) {
      return;
    }

    if (posthog.__loaded) {
      posthog.identify(traits?.id, {
        email: traits?.email,
        firstName: traits?.firstName || '',
        lastName: traits?.lastName || '',
        language: traits?.language || '',
        role: traits?.role || '',
        roles: traits?.roles || [],
        tenantId: traits?.tenantId || '',
        companyId: traits?.companyId || '',
      });
    }
  }
  async reset() {
    if (!this.enabled) {
      return;
    }

    if (typeof window !== 'undefined') {
      window.EncTracking = window.EncTracking || {};
      window.EncTracking.clearCookies?.();
    }

    if (posthog.__loaded) {
      posthog.reset(true);
    }
  }
}
